const POINTER_SAMPLE_INTERVAL_MS = 125;
const POINTER_BATCH_INTERVAL_MS = 2000;
const POINTER_BATCH_SIZE = 25;
const BEHAVIOR_FLUSH_INTERVAL_MS = 5000;
const HOVER_DWELL_THRESHOLD_MS = 120;
const SCROLL_THRESHOLDS = [10, 25, 50, 75, 90, 100];
const DETAILED_BEHAVIOR_TELEMETRY_ENABLED =
  process.env.REACT_APP_FUNNEL_DETAILED_BEHAVIOR_TELEMETRY === 'true';

function hasWindow() {
  return typeof window !== 'undefined';
}

function closestElement(target, selector) {
  return target instanceof Element ? target.closest(selector) : null;
}

function getTrackId(target) {
  const element = closestElement(target, '[data-track-id]');
  return element?.getAttribute('data-track-id') || '';
}

function getSectionId(target) {
  const section = closestElement(target, '[data-track-section]');
  return section?.getAttribute('data-track-section') || '';
}

function isInteractive(target) {
  return Boolean(
    closestElement(
      target,
      'a, button, input, textarea, select, summary, [role="button"], [role="link"], [data-track-action]'
    )
  );
}

function getScrollPercentage() {
  const root = document.documentElement;
  const scrollTop = window.scrollY || root.scrollTop || 0;
  const scrollable = Math.max(1, root.scrollHeight - window.innerHeight);
  return Math.min(100, Math.round((scrollTop / scrollable) * 100));
}

function getElementRectPayload(element) {
  const rect = element.getBoundingClientRect();

  return {
    top: Math.round(rect.top),
    left: Math.round(rect.left),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  };
}

function now() {
  return performance.now();
}

function trackAsync(tracker, eventName, payload, options) {
  tracker.track(eventName, payload, options).catch(() => {});
}

export function startBehaviorTelemetry({ root, tracker }) {
  if (!hasWindow() || !root) {
    return () => {};
  }

  let lastPointerSample = 0;
  let pointerSamples = [];
  let recentClicks = [];
  const scrollMilestones = new Set();
  const activeHovers = new Map();
  const visibleSections = new Map();
  const viewedSections = new Set();
  const viewedCtas = new Set();

  function flushQueuedEvents() {
    tracker.flush({ timeoutMs: 1200 }).catch(() => {});
  }

  function flushPointerSamples() {
    if (pointerSamples.length === 0) {
      return;
    }

    const samples = pointerSamples;
    pointerSamples = [];

    trackAsync(tracker, 'pointer_sample_batch', {
      sample_count: samples.length,
      samples,
    });
  }

  function samplePointer(event) {
    const sampleTime = now();
    if (sampleTime - lastPointerSample < POINTER_SAMPLE_INTERVAL_MS) {
      return;
    }

    lastPointerSample = sampleTime;

    pointerSamples.push({
      x: Number((event.clientX / Math.max(1, window.innerWidth)).toFixed(4)),
      y: Number((event.clientY / Math.max(1, window.innerHeight)).toFixed(4)),
      scroll_y: Math.round(window.scrollY || 0),
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      pointer_type: event.pointerType || 'mouse',
      section_id: getSectionId(event.target),
      target_tracking_id: getTrackId(event.target),
    });

    if (pointerSamples.length >= POINTER_BATCH_SIZE) {
      flushPointerSamples();
    }
  }

  function recordScrollDepth() {
    const percentage = getScrollPercentage();

    SCROLL_THRESHOLDS.forEach((threshold) => {
      if (percentage >= threshold && !scrollMilestones.has(threshold)) {
        scrollMilestones.add(threshold);
        trackAsync(tracker, 'scroll_depth', {
          threshold,
          percentage,
        });
      }
    });
  }

  function recordClick(event) {
    const clickTime = now();
    const trackId = getTrackId(event.target);
    const sectionId = getSectionId(event.target);
    const interactive = isInteractive(event.target);

    activeHovers.forEach((hover) => {
      if (hover.element.contains(event.target)) {
        hover.clicked = true;
        hover.clickLatencyMs = Math.round(clickTime - hover.startedAt);
      }
    });

    trackAsync(tracker, 'click', {
      element_id: trackId,
      tag_name: event.target?.tagName ? event.target.tagName.toLowerCase() : '',
      x: event.clientX,
      y: event.clientY,
      section_id: sectionId,
      interactive,
    });

    if (!interactive) {
      trackAsync(tracker, 'dead_click', {
        element_id: trackId,
        x: event.clientX,
        y: event.clientY,
        section_id: sectionId,
      });
    }

    recentClicks = recentClicks
      .filter((click) => clickTime - click.time < 900)
      .concat({ x: event.clientX, y: event.clientY, time: clickTime });

    const nearbyClicks = recentClicks.filter((click) => {
      const deltaX = click.x - event.clientX;
      const deltaY = click.y - event.clientY;
      return Math.sqrt(deltaX * deltaX + deltaY * deltaY) <= 32;
    });

    if (nearbyClicks.length >= 3) {
      trackAsync(tracker, 'rage_click', {
        element_id: trackId,
        click_count: nearbyClicks.length,
        x: event.clientX,
        y: event.clientY,
        section_id: sectionId,
      });
      recentClicks = [];
    }
  }

  function startHover(event) {
    const element = closestElement(event.target, '[data-track-hover]');
    if (!element || activeHovers.has(element)) {
      return;
    }

    const payload = {
      element_id: element.getAttribute('data-track-id') || '',
      hover_id: element.getAttribute('data-track-hover') || '',
      section_id: getSectionId(element),
    };

    activeHovers.set(element, {
      element,
      startedAt: now(),
      payload,
      clicked: false,
      clickLatencyMs: null,
    });

    trackAsync(tracker, 'hover_start', payload);
  }

  function endHover(event) {
    const element = closestElement(event.target, '[data-track-hover]');
    if (!element || element.contains(event.relatedTarget)) {
      return;
    }

    const hover = activeHovers.get(element);
    if (!hover) {
      return;
    }

    activeHovers.delete(element);

    const durationMs = Math.round(now() - hover.startedAt);
    const payload = {
      ...hover.payload,
      duration_ms: durationMs,
      preceded_click: hover.clicked,
      hover_to_click_latency_ms: hover.clickLatencyMs,
    };

    trackAsync(tracker, 'hover_end', payload);

    if (durationMs >= HOVER_DWELL_THRESHOLD_MS) {
      trackAsync(tracker, 'hover_dwell', payload);

      if (element.hasAttribute('data-track-cta')) {
        trackAsync(tracker, 'cta_hover', payload);
      }
    }
  }

  function flushVisibleSections(reason = 'visibility_change') {
    const flushTime = now();

    visibleSections.forEach((visible, sectionId) => {
      const durationMs = Math.round(visible.totalMs + (flushTime - visible.startedAt));
      if (durationMs > 0) {
        trackAsync(tracker, 'section_engaged_time', {
          section_id: sectionId,
          duration_ms: durationMs,
          reason,
        });
      }
      visibleSections.set(sectionId, { startedAt: flushTime, totalMs: 0 });
    });
  }

  const sectionObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const sectionId = entry.target.getAttribute('data-track-section') || '';

              if (entry.isIntersecting) {
                if (!viewedSections.has(sectionId)) {
                  viewedSections.add(sectionId);
                  trackAsync(tracker, 'section_view', {
                    section_id: sectionId,
                    visible_ratio: Number(entry.intersectionRatio.toFixed(2)),
                  });
                }

                if (!visibleSections.has(sectionId)) {
                  visibleSections.set(sectionId, { startedAt: now(), totalMs: 0 });
                }
              } else if (visibleSections.has(sectionId)) {
                const visible = visibleSections.get(sectionId);
                const durationMs = Math.round(visible.totalMs + (now() - visible.startedAt));
                visibleSections.delete(sectionId);

                if (durationMs > 0) {
                  trackAsync(tracker, 'section_engaged_time', {
                    section_id: sectionId,
                    duration_ms: durationMs,
                    reason: 'section_exit',
                  });
                }
              }
            });
          },
          { threshold: [0.45] }
        )
      : null;

  const ctaObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                return;
              }

              const ctaId = entry.target.getAttribute('data-track-id') || '';
              if (viewedCtas.has(ctaId)) {
                return;
              }

              viewedCtas.add(ctaId);
              trackAsync(tracker, 'cta_impression', {
                element_id: ctaId,
                section_id: getSectionId(entry.target),
                rect: getElementRectPayload(entry.target),
              });
            });
          },
          { threshold: [0.55] }
        )
      : null;

  function recordVisibilityChange() {
    trackAsync(tracker, 'page_visibility_change', {
      visibility_state: document.visibilityState,
    });

    if (document.visibilityState === 'hidden') {
      flushVisibleSections('page_hidden');
      flushPointerSamples();
      tracker.flush({ timeoutMs: 600, preferBeacon: true }).catch(() => {});
    }
  }

  function recordPageExit() {
    flushVisibleSections('page_exit');
    flushPointerSamples();
    trackAsync(tracker, 'page_exit', {
      visibility_state: document.visibilityState,
    });
    tracker.flush({ timeoutMs: 600, preferBeacon: true }).catch(() => {});
  }

  if (DETAILED_BEHAVIOR_TELEMETRY_ENABLED) {
    root.addEventListener('pointermove', samplePointer, { passive: true });
    root.addEventListener('pointerover', startHover, { passive: true });
    root.addEventListener('pointerout', endHover, { passive: true });
  }
  root.addEventListener('click', recordClick, { passive: true });
  window.addEventListener('scroll', recordScrollDepth, { passive: true });
  document.addEventListener('visibilitychange', recordVisibilityChange);
  window.addEventListener('pagehide', recordPageExit);
  window.addEventListener('beforeunload', recordPageExit);

  const batchTimer = DETAILED_BEHAVIOR_TELEMETRY_ENABLED
    ? window.setInterval(flushPointerSamples, POINTER_BATCH_INTERVAL_MS)
    : null;
  const queueFlushTimer = window.setInterval(flushQueuedEvents, BEHAVIOR_FLUSH_INTERVAL_MS);

  sectionObserver &&
    root.querySelectorAll('[data-track-section]').forEach((section) => {
      sectionObserver.observe(section);
    });

  ctaObserver &&
    root.querySelectorAll('[data-track-cta]').forEach((cta) => {
      ctaObserver.observe(cta);
    });

  recordScrollDepth();

  return () => {
    flushVisibleSections('cleanup');
    flushPointerSamples();
    if (batchTimer) {
      window.clearInterval(batchTimer);
    }
    window.clearInterval(queueFlushTimer);
    if (DETAILED_BEHAVIOR_TELEMETRY_ENABLED) {
      root.removeEventListener('pointermove', samplePointer);
      root.removeEventListener('pointerover', startHover);
      root.removeEventListener('pointerout', endHover);
    }
    root.removeEventListener('click', recordClick);
    window.removeEventListener('scroll', recordScrollDepth);
    document.removeEventListener('visibilitychange', recordVisibilityChange);
    window.removeEventListener('pagehide', recordPageExit);
    window.removeEventListener('beforeunload', recordPageExit);
    sectionObserver?.disconnect();
    ctaObserver?.disconnect();
  };
}
